/**
 * 五行能量详析引擎
 * 提供五行各维度的深度解读数据：
 * - 性格与气质、事业与财运、感情与人际、健康与养生、调节建议
 * 按五行强弱（过旺/旺盛/适中/偏弱/极弱）分级给出个性化描述
 * 遵循"温和表述"原则，不利结果提供化解方向
 */

import {
  type Branch,
  type Element,
  type FourPillars,
  STEM_ELEMENT,
  BRANCH_HIDDEN,
} from '../../baziCalc';

// ── 强弱等级 ─────────────────────────────────────────────────
export type StrengthLevel = '过旺' | '旺盛' | '适中' | '偏弱' | '极弱';

export function getStrengthLevel(percent: number): StrengthLevel {
  if (percent >= 35) return '过旺';
  if (percent >= 25) return '旺盛';
  if (percent >= 18) return '适中';
  if (percent >= 12) return '偏弱';
  return '极弱';
}

export function getStrengthColor(level: StrengthLevel): string {
  switch (level) {
    case '过旺': return '#D14B45';
    case '旺盛': return '#E8A020';
    case '适中': return '#7BC67E';
    case '偏弱': return '#4A90D9';
    case '极弱': return '#8A7B91';
  }
}

// ── 五行基础信息 ─────────────────────────────────────────────
export const ELEMENT_COLORS: Record<Element, string> = {
  木: '#7BC67E', 火: '#D14B45', 土: '#A9744F', 金: '#D4AF37', 水: '#4A90D9',
};

export const ELEMENT_ICONS: Record<Element, string> = {
  木: 'ri-plant-line', 火: 'ri-fire-line', 土: 'ri-landscape-line',
  金: 'ri-medal-line', 水: 'ri-drop-line',
};

// ── 五行固定属性 ─────────────────────────────────────────────
export interface ElementFixedInfo {
  symbol: string;        // 象征
  keyword: string;       // 关键词
  season: string;        // 季节
  direction: string;     // 方位
  taste: string;         // 味道（五味）
  emotion: string;       // 情志
  color: string;         // 代表颜色
  organ: string;         // 主脏
  organRelated: string;  // 相关器官
  generates: Element;    // 我生
  overcomes: Element;    // 我克
  generatedBy: Element;  // 生我
  overcomesBy: Element;  // 克我
  careers: string[];     // 适合职业
  boostTips: string[];   // 增强方法（用于弱时）
  reduceTips: string[];  // 降低方法（用于旺时）
}

export const ELEMENT_FIXED: Record<Element, ElementFixedInfo> = {
  木: {
    symbol: '大树、竹林、花草',
    keyword: '生长 · 创造 · 仁爱',
    season: '春',
    direction: '东方',
    taste: '酸',
    emotion: '怒（激情、冲劲）',
    color: '绿色、青色',
    organ: '肝',
    organRelated: '胆、筋、眼',
    generates: '火',
    overcomes: '土',
    generatedBy: '水',
    overcomesBy: '金',
    careers: ['教育培训', '文学创作', '医疗健康', '环保园艺', '设计规划', '心理咨询', '法律'],
    boostTips: ['多在树林、公园中散步，亲近绿色植物', '在家中摆放绿植，使用绿色、青色装饰', '多食酸味食物：醋、柑橘、绿叶蔬菜', '向东方（木方位）发展，东方城市更适合', '锻炼手脚灵活性，瑜伽、太极有助木气'],
    reduceTips: ['减少绿色、木质装饰，增加金属材质元素', '生活中加入白色、银色，引入金气制木', '适当减少酸味食物，调和饮食', '多练习冥想，减少冲动情绪', '增加纪律性活动，如计划管理、结构化工作'],
  },
  火: {
    symbol: '太阳、炉火、烛光',
    keyword: '热情 · 表达 · 领导',
    season: '夏',
    direction: '南方',
    taste: '苦',
    emotion: '喜（热情、喜悦）',
    color: '红色、橙色、紫色',
    organ: '心',
    organRelated: '小肠、血脉、舌',
    generates: '土',
    overcomes: '金',
    generatedBy: '木',
    overcomesBy: '水',
    careers: ['演艺娱乐', '市场营销', '领导管理', '公关活动', '餐饮烹饪', '宗教信仰', '体育竞技'],
    boostTips: ['多晒阳光，南方向阳的居住环境更佳', '穿着红色、橙色等暖色系服装', '参与社交活动，增加人际互动', '多食辣、苦味食物：辣椒、苦瓜', '培养兴趣爱好，激发内心热情'],
    reduceTips: ['增加水元素：多饮水，近水源居住', '使用蓝色、黑色冷色调装饰', '练习冥想、正念，平稳情绪', '减少辛辣食物摄入，饮食清淡', '增加静思独处时间，不追求过度刺激'],
  },
  土: {
    symbol: '大地、山丘、田园',
    keyword: '稳定 · 包容 · 信用',
    season: '四季末（每季最后18天）',
    direction: '中央',
    taste: '甘甜',
    emotion: '思（思虑、专注）',
    color: '黄色、棕色、米色',
    organ: '脾',
    organRelated: '胃、肌肉、口',
    generates: '金',
    overcomes: '水',
    generatedBy: '火',
    overcomesBy: '木',
    careers: ['房地产', '农业食品', '财务会计', '行政管理', '银行金融', '物流仓储', '传统手工艺'],
    boostTips: ['多接触大地，郊游、农耕、园艺活动', '居住环境使用黄色、土黄色系装饰', '多食甘甜食物：红薯、南瓜、玉米', '向中原、内陆地区发展更利运势', '培养踏实稳定的生活习惯与规律作息'],
    reduceTips: ['增加木元素：多亲近树木自然，种植绿植', '服装减少黄色，增加绿色、青色', '适当减少甜食，控制体重', '增加运动量，避免久坐', '拓宽思维，尝试新鲜事物，防止固步自封'],
  },
  金: {
    symbol: '宝剑、珠宝、金属',
    keyword: '果断 · 纪律 · 精准',
    season: '秋',
    direction: '西方',
    taste: '辛辣',
    emotion: '悲（敏锐、深思）',
    color: '白色、银色、金色',
    organ: '肺',
    organRelated: '大肠、皮肤、鼻',
    generates: '水',
    overcomes: '木',
    generatedBy: '土',
    overcomesBy: '火',
    careers: ['金融证券', '法律仲裁', '工程技术', '珠宝首饰', '军警执法', '外科医学', '汽车机械'],
    boostTips: ['佩戴金属饰品：黄金、白银、钢铁材质', '居住环境使用白色、银色、金属质感装饰', '多食辛味食物：葱、姜、辣椒', '向西方发展，西部城市更利金气', '培养精准、有条理的工作与生活方式'],
    reduceTips: ['增加火元素：多晒太阳，使用暖色调装饰', '减少金属饰品，服装换为红色、橙色', '适当减少辛辣食物', '练习柔和表达，减少强硬语气', '增加温情人际互动，学会示弱与合作'],
  },
  水: {
    symbol: '江海、雨露、泉水',
    keyword: '智慧 · 灵活 · 洞察',
    season: '冬',
    direction: '北方',
    taste: '咸',
    emotion: '恐（直觉、深度）',
    color: '黑色、蓝色、深灰',
    organ: '肾',
    organRelated: '膀胱、骨骼、耳',
    generates: '木',
    overcomes: '火',
    generatedBy: '金',
    overcomesBy: '土',
    careers: ['哲学研究', '心理咨询', '信息技术', '外交使节', '艺术文学', '旅游探险', '历史考古'],
    boostTips: ['居住或工作地点靠近水源：河流、湖泊、海边', '使用黑色、深蓝色装饰，增加水元素感', '多食咸味、黑色食物：黑芝麻、海产品', '向北方发展，滨海城市更利水气', '保暖是关键，避免寒湿侵体，注意肾部保养'],
    reduceTips: ['增加土元素：多亲近山地、平原环境', '居住使用黄色、棕色暖色调', '减少咸食，饮食清淡，保护肾功能', '增加行动力，减少过度分析思虑', '培养稳定的生活规律，减少情绪起伏'],
  },
};

// ── 分级详析 ─────────────────────────────────────────────────
interface LevelAnalysis {
  title: string;
  summary: string;
  personality: string;
  career: string;
  emotion: string;
  health: string;
  balance: string;
}

type ElementLevelData = Record<StrengthLevel, LevelAnalysis>;

const WOOD_LEVELS: ElementLevelData = {
  过旺: {
    title: '木气过旺',
    summary: '您的命局中木气能量极为充沛，如参天大树，生机勃勃，但也需要懂得收束才能枝繁叶茂。',
    personality: '思想开明，进取心极强，富有创意与想象力，但有时会因过于坚持己见而显得固执。容易对他人产生过高期望，情绪上容易急躁，建议在保持热情的同时，多一分灵活与包容。',
    career: '极具开拓精神，最适合创业、开发新领域或担任团队领导。木气旺则宜疏导——选择需要创意和开拓的行业，才能将旺盛木气转化为实际成就。金融、纪律性工作可以帮助平衡。',
    emotion: '感情中充满热情，对伴侣关爱有加，但也容易主观强势，偶尔会让对方感到压力。学会倾听和接受不同意见，感情会更和谐稳定。',
    health: '肝胆功能偏旺，需注意防止肝火过旺引起的情绪急躁、眼睛干涩、失眠等问题。建议饮食中减少酸味和油腻，多食疏肝的食物如菊花茶、柴胡等。保持情绪平稳是最好的护肝方式。以上健康提示仅为命理参考，如有不适请及时咨询专业医生。',
    balance: '可适当引入"金克木"的力量：佩戴金属饰品、使用白色银色装饰，增强纪律感与规则意识。减少绿色装扮，让旺盛的木气得到合理疏导而非持续累积。命理是了解自己的工具，积极调整才是最好的优化。',
  },
  旺盛: {
    title: '木气旺盛',
    summary: '木气旺盛，如枝叶繁茂的大树，生命力充沛，创造力与行动力俱佳，是命局的重要优势。',
    personality: '充满活力与创造力，思维敏捷，对新鲜事物充满好奇。具有良好的规划能力，做事有始有终，人缘较好。偶尔会有些理想化，但整体来说是非常积极正面的性格特质。',
    career: '事业运旺，适合教育、文化、创意、医疗、法律等木气相关行业。领导力自然流露，是团队中的重要推动力。建议在稳步发展中保持耐心，不要因过于心急而错失机会。',
    emotion: '感情真诚专一，对家人和伴侣关爱备至。具有天然的保护欲和责任感，是可信赖的伴侣。',
    health: '肝胆状态良好，整体健康状况佳。建议保持规律运动，避免长期久坐影响肝胆气机流通。以上健康提示仅为命理参考，如有不适请及时咨询专业医生。',
    balance: '木气旺盛是优势，适当维持即可。注意不要让旺盛的木气变成固执，在坚持中保留弹性，是最好的状态。相信自己的能量，同时也给自己留有调整空间。',
  },
  适中: {
    title: '木气适中',
    summary: '木气均衡，恰到好处，进取心与平和心并存，是最理想的五行状态之一。',
    personality: '性格平和而有弹性，既有进取心，又懂得放松与享受。处事圆融，善于在坚持原则与灵活变通之间找到平衡，是成熟稳重的表现。',
    career: '各方面发展均衡，木气相关行业（教育、医疗、创意）均有发挥空间。事业稳步推进，不急不缓，踏实可靠。',
    emotion: '感情生活和谐，有良好的沟通能力，懂得在关系中给予空间，也懂得主动付出。',
    health: '肝胆功能平衡，整体健康状况良好。维持当前健康习惯，保持情绪稳定即可。以上健康提示仅为命理参考，如有不适请及时咨询专业医生。',
    balance: '木气适中是理想状态，保持即可。若感觉某阶段木气需要增强，可以多亲近自然、绿色植物。您的平衡本身就是一种能量。',
  },
  偏弱: {
    title: '木气偏弱',
    summary: '木气稍显不足，就像一棵需要更多阳光和雨水的幼苗，通过调养可以让木气逐渐充盈。',
    personality: '有时会感到缺乏行动力或决断力，对新事物的探索欲望偏低。内心深处可能有些犹豫不决，建议通过小步前进积累自信，每一次勇敢迈出都是对木气的补充。',
    career: '可以通过选择木气行业（教育、创意、医疗）来激活和补充木气。建议主动寻找成长机会，给自己设定小目标，逐步增强行动力和进取心。',
    emotion: '在感情中可能略显被动，建议主动表达心意，不要等待对方先迈出第一步。感情的主动经营是增强木气的有效方式。',
    health: '肝胆需要适当关注，可能出现情绪压抑、肌肉酸软等问题。建议增加户外活动，多接触自然绿色，适当补充维生素B族，养护肝脏。以上健康提示仅为命理参考，如有不适请及时咨询专业医生。',
    balance: '建议多亲近木气环境：在树林、公园散步，家中摆放绿植，穿着绿色系服装。食物上可适当增加酸味：柑橘、醋类，逐步补充木气。每一步调整都在让您更接近理想状态。',
  },
  极弱: {
    title: '木气极弱',
    summary: '木气极为稀薄，生机之力有待激活。这不是局限，而是提醒您通过后天调养来唤醒内在的成长能量。',
    personality: '在面对挑战时可能缺乏冲劲，容易犹豫不决。但极弱的木气往往造就温和细腻的性格，擅长配合他人、接受建议。通过有意识地培养规划力和行动力，可以逐步改善。',
    career: '建议选择有团队支持、结构清晰的工作环境，借助外部框架来弥补内在动力的不足。同时主动涉足木气行业，以职业发展来滋养木气。',
    emotion: '感情上善于体谅他人，但需要主动表达自己的需求，避免一味迁就导致失去自我。找到自己的节奏，感情会更加平衡。',
    health: '肝胆功能需要重点关注，建议定期体检，注意眼睛、筋脉的健康。多食绿色蔬菜、护肝食物如枸杞、菊花，避免酒精对肝脏的损害。以上健康提示仅为命理参考，如有不适请及时咨询专业医生。',
    balance: '大力补充木气：每天至少30分钟户外绿色环境；家居增加大量绿植；服装以绿色为主；食物多选酸味和绿色食品。命理是指引，积极的生活方式是最好的五行调节。记住：后天的调养完全可以弥补先天的不足。',
  },
};

const FIRE_LEVELS: ElementLevelData = {
  过旺: {
    title: '火气过旺',
    summary: '命局火气如烈日当空，热情充沛，但也需要懂得收敛，才能持续发光发热而不竭。',
    personality: '热情奔放，极具感召力，天生的领袖气质让人信服。但有时可能过于急躁，遇事容易冲动，说话也可能直接伤人而不自知。学会在热情中加入一份冷静，会让你的魅力更辅助久。',
    career: '极具表现欲和领导力，适合演艺、公关、管理等舞台。火旺则宜"疏导"：将旺盛激情转化为创造力与行动力。建议在高强度工作中注意休息，防止精力透支。',
    emotion: '感情炽热，给伴侣满满的安全感和热情。但需要注意，过于浓烈的情感有时会让对方感到窒息。学会给感情留白，让彼此都有呼吸的空间。',
    health: '心脑血管需要重点关注，火旺容易引起情绪激动、失眠、心跳加速等问题。建议减少辛辣刺激食物，多食苦瓜、芹菜等清心降火食物，保持良好的睡眠习惯。以上健康提示仅为命理参考，如有不适请及时咨询专业医生。',
    balance: '引入水元素来平衡：多饮水、亲近水源、使用蓝色黑色装饰。放慢节奏，每天冥想10-15分钟，让过旺的火气得到良性疏导。这是爱护自己的方式，不是限制。',
  },
  旺盛: {
    title: '火气旺盛',
    summary: '火气旺盛，热情与活力是你最大的财富，散发自然领导魅力，是令人振奋的命局特质。',
    personality: '积极乐观，充满能量，无论走到哪里都是人群中的焦点。表达能力强，善于激励他人，是天然的团队建设者。保持这份热情，同时培养一份耐心，会让你走得更远。',
    career: '适合任何需要热情展示和人际互动的领域。事业运旺，尤其在公众场合、领导岗位或创意行业大放异彩。',
    emotion: '感情生活热烈美好，你的热情让伴侣备受珍视。注意在激情之外增加一份细水长流的温柔。',
    health: '整体活力充沛，注意心脏保健，避免过度劳累。规律作息是保护旺盛火气的最好方式。以上健康提示仅为命理参考，如有不适请及时咨询专业医生。',
    balance: '火气旺盛是优势，保持适度平衡即可。在强烈激情中融入一份沉静，是提升气质的关键。相信自己的热情，也给自己留一点冷静的空间。',
  },
  适中: {
    title: '火气适中',
    summary: '火气均衡，你拥有恰到好处的热情与活力，既能激情四射，也能沉静思考，是理想状态。',
    personality: '性格开朗而不失稳重，热情而不失理性。处事积极乐观，善于鼓励他人，同时也有自己冷静思考的一面。',
    career: '各方面发展均衡，既适合需要热情展示的岗位，也能胜任需要耐心和细致的工作。',
    emotion: '感情平衡健康，既有激情也有温情，是感情路上的好伴侣。',
    health: '心血管状态良好，保持当前健康习惯，适当运动，规律作息即可。以上健康提示仅为命理参考，如有不适请及时咨询专业医生。',
    balance: '火气适中已是理想，保持即可。若感到能量不足，可增加社交、运动来激活内在热情。您的平衡就是最好的状态。',
  },
  偏弱: {
    title: '火气偏弱',
    summary: '火气偏弱，就像一盏需要添油的灯，通过积极调养可以点亮你内在的热情与活力。',
    personality: '可能在公众场合略显内敛，社交时需要一些热身才能进入状态。这种特质让你善于深度思考和独处，是一种内在的宝贵力量。通过主动参与社交活动，可以逐步激活火气能量。',
    career: '建议选择有利于展示和表达的机会，主动争取上台发言或领导小项目，用实践来激活内在的火气能量。',
    emotion: '感情中可能需要更多勇气表达心意。对方可能看不到你内心的热情，建议主动一些，让感情更有活力。',
    health: '需要关注心脑血管健康，适当增加有氧运动如慢跑、游泳。多晒太阳、保持充足睡眠对补充火气很有帮助。以上健康提示仅为命理参考，如有不适请及时咨询专业医生。',
    balance: '通过生活方式补充火气：多接触阳光；穿着红色、橙色暖色系；参加社交活动增加互动；多食辣椒、韭菜等温热食物；南方或阳光充足地区更适合居住。每一步调整都在唤醒您内在的热情。',
  },
  极弱: {
    title: '火气极弱',
    summary: '火气极为微弱，如同深夜的星光，虽小却有，需要用心呵护和培养，让内心的热情逐渐绽放。',
    personality: '内向安静，享受独处，思维细腻深邃。这种特质让你特别善于观察和感受，是艺术家和思考者的珍贵气质。建议有意识地走出舒适区，逐步激活内在的表达欲。',
    career: '建议选择能够在小范围内发挥影响力的工作起步，逐步拓展。加入兴趣社群、参加培训课程，都是激活火气的有效方式。',
    emotion: '感情上需要特别关注，可能因表达不足而让对方感到疏远。建议主动创造浪漫时刻，让伴侣感受到你的情感温度。',
    health: '心脑血管健康需要重点关注，建议定期体检。增加户外活动，多晒阳光，避免长期宅居导致阳气不足。温阳食物如姜、红枣对补充火气有益。以上健康提示仅为命理参考，如有不适请及时咨询专业医生。',
    balance: '全面补充火气：每天至少30分钟阳光照射；家居使用红色、橙色暖色调；多参加社交活动和集体运动；食物多选温热性质；南方阳光地带居住最佳。命理是指引，后天的调养完全可以点燃内在的热情之火。',
  },
};

const EARTH_LEVELS: ElementLevelData = {
  过旺: {
    title: '土气过旺',
    summary: '土气过旺，如厚重的大地，稳定踏实的同时，也需要注意不要因过于"稳"而失去变通与活力。',
    personality: '为人厚道可靠，信用极佳，是大家眼中最值得托付的人。但有时过于保守固执，对变化缺乏适应力，容易因墨守成规而错失机会。学会在稳定中追求创新，是重要的成长方向。',
    career: '在需要稳定性和可靠性的行业表现优秀，如金融、房地产、行政管理。建议主动拓展眼界，接受新思路，防止因保守而被时代淘汰。',
    emotion: '感情中极为忠诚稳定，是坚实可靠的伴侣。但有时情感表达过于平静，缺乏浪漫与惊喜。尝试增加一些温柔的小举动，让感情保持新鲜。',
    health: '脾胃功能可能因过旺而出现消化问题，如腹胀、消化不良。建议少食甜腻厚重食物，增加运动，避免久坐，让气机流通更顺畅。以上健康提示仅为命理参考，如有不适请及时咨询专业医生。',
    balance: '引入木气来疏导：多亲近自然绿色、增加绿植装饰。保持活跃的社交生活，多尝试新事物，用"木疏土"的原理让生活更有流动感。稳定是优势，流动是提升。',
  },
  旺盛: {
    title: '土气旺盛',
    summary: '土气旺盛，踏实厚重，是命局中稳定的基石，财运、信用、人际关系都得到土气的滋养。',
    personality: '性格温和包容，处事稳重可靠，具有天然的亲和力。善于聆听他人，是朋友圈中的定心丸。重视实际成果，不爱虚名，踏踏实实的做事风格赢得广泛尊重。',
    career: '财运稳健，适合需要耐心积累的行业。土旺之人往往在中年以后迎来最丰盛的阶段，厚积薄发是你的节奏。',
    emotion: '感情稳定长久，重视家庭责任，是伴侣眼中最可靠的依靠。在稳定中也可以加一些温柔浪漫的小惊喜。',
    health: '脾胃功能良好，注意饮食规律，避免暴饮暴食。保持适量运动，防止过于安逸影响新陈代谢。以上健康提示仅为命理参考，如有不适请及时咨询专业医生。',
    balance: '土气旺盛本是优势，保持活跃是关键。避免过于安于现状，保持前进的动力和对新事物的好奇心。您的厚重是基础，流动是成长。',
  },
  适中: {
    title: '土气适中',
    summary: '土气均衡，稳定与活力兼备，是踏实与灵活的完美平衡，命局中理想的土气状态。',
    personality: '性格温和而有弹性，既有大地般的踏实，又不缺少灵活应变的能力。处事公正，善于在稳中求进，是众人信赖的可靠之人。',
    career: '各行业均可发展，尤其擅长需要耐心和积累的领域。事业稳步上升，不冒进也不保守。',
    emotion: '感情关系和谐稳定，懂得在关系中承担责任，也懂得给彼此空间。',
    health: '脾胃状态平衡，消化良好，保持规律饮食和适量运动即可。以上健康提示仅为命理参考，如有不适请及时咨询专业医生。',
    balance: '土气适中是理想状态，维持当前生活节奏即可。您的稳定本身就是一种强大的能量。',
  },
  偏弱: {
    title: '土气偏弱',
    summary: '土气稍显不足，根基有待加强，通过调养可以逐步建立内心的安全感与稳定感。',
    personality: '可能在面对压力时感到根基不稳，缺乏安全感，容易为不确定的未来感到焦虑。这是提醒你建立内心稳定性的信号。通过培养踏实的日常习惯，可以逐步增强土气基础。',
    career: '建议选择有稳定结构的工作环境，避免频繁跳槽。脚踏实地比追逐机会更重要，每一步踏实的努力都是在补充土气。',
    emotion: '感情中可能对稳定和安全感有更多渴望，建议主动建立信任关系，不要因不安全感而对伴侣过于依赖。',
    health: '脾胃功能需要关注，可能出现消化不良、食欲不稳等问题。建议规律饮食，多食温暖易消化的食物如小米粥、南瓜、红薯。以上健康提示仅为命理参考，如有不适请及时咨询专业医生。',
    balance: '通过生活方式补充土气：接触大地（赤脚走草地）；家居使用黄色、棕色系；多食甘甜食物；中部内陆地区更利土气；培养稳定的作息和日常仪式感。稳定感是可以一点点建立的。',
  },
  极弱: {
    title: '土气极弱',
    summary: '土气极为稀薄，如同需要肥沃土壤的种子，补充土气将帮助你建立生活的稳定基础。',
    personality: '可能感到缺乏脚踏实地的力量，生活节奏较难稳定。但土气弱往往造就灵活多变、思维跳脱的特质，是另一种宝贵的能量。有意识地培养规律性，可以弥补土气的不足。',
    career: '建议明确长期目标，建立稳定的职业规划。选择有导师或结构化成长路径的工作，借助外部框架补充内在稳定性的不足。',
    emotion: '感情中需要建立稳定的信任基础，避免因缺乏安全感而频繁动摇。找到一个踏实可靠的伴侣，对土气极弱的人来说尤为重要。',
    health: '脾胃需要重点养护，建议减少生冷食物，坚持规律三餐。可适当服用补脾健胃的食疗：山药、薏仁、大枣等。以上健康提示仅为命理参考，如有不适请及时咨询专业医生。',
    balance: '全面补充土气：每天接触大地；家居以暖黄色调为主；规律三餐，多食甘甜食物；定居内陆城市更适合；最重要的是建立稳定的生活仪式，让每一天都有踏实的节奏。后天的规律可以重建先天缺失的稳定感。',
  },
};

const METAL_LEVELS: ElementLevelData = {
  过旺: {
    title: '金气过旺',
    summary: '金气过旺，如宝剑锋芒毕露，果断决绝，但也需要一份柔和，才能既锋利又耐久。',
    personality: '行事雷厉风行，极具决断力和执行力，是天生的行动派。但有时过于强硬，处理事情过于直接，容易与他人产生摩擦。学习在果断中融入柔和，在直接中加入温情，会让你的人缘更好。',
    career: '极强的执行力让你在需要快速决策和行动的行业如鱼得水。建议在强势风格中加入倾听和协作，团队合作将极大放大你的能力。',
    emotion: '感情中保护欲强，对家人极为负责。但有时说话太直接，容易伤害到敏感的伴侣。学会柔化语气，用温柔包裹你的爱，感情会更加甜蜜。',
    health: '肺部和大肠需要关注，金旺容易引起皮肤干燥、呼吸道敏感等问题。建议多饮水，保持环境湿润，减少辛辣刺激食物。以上健康提示仅为命理参考，如有不适请及时咨询专业医生。',
    balance: '引入火元素来平衡：多晒太阳，使用红色暖色系装饰，增加社交活动。减少金属饰品，让过旺的金气得到温柔的疏导。刚柔并济才是最持久的力量。',
  },
  旺盛: {
    title: '金气旺盛',
    summary: '金气旺盛，果断精准，是命局中行动力的源泉，财运与执行力俱佳。',
    personality: '行事有条不紊，意志坚定，不轻易被外界干扰。具有良好的财务观念和纪律性，是自律型的优秀人才。偶尔显得有些严肃，在工作与生活中保持一些轻松感，会让你更受欢迎。',
    career: '适合金融、法律、工程、管理等需要精准和执行力的领域。财运较旺，通过努力可以积累可观的财富。',
    emotion: '感情中忠诚可靠，重视承诺，是令人安心的伴侣。在稳定的关系中增加一些浪漫与惊喜，感情会更加丰盈。',
    health: '整体健康，注意保持呼吸系统健康，避免污浊空气环境。以上健康提示仅为命理参考，如有不适请及时咨询专业医生。',
    balance: '金气旺盛是优势，保持平衡即可。在强硬中融入柔和，是人生功课。您的执行力是宝贵的，加入一点温度会让它更完整。',
  },
  适中: {
    title: '金气适中',
    summary: '金气均衡，果断与柔和并存，精准而不失温情，是命局中理想的金气状态。',
    personality: '有原则有底线，同时也有温柔灵活的一面。处事公正，既能坚守立场，也懂得顾及他人感受。',
    career: '金气相关行业均有发挥空间，工作中既有执行力又有人情味。',
    emotion: '感情关系均衡，既有稳定的承诺，也有温柔的关怀。',
    health: '肺肠状态良好，保持良好呼吸习惯和规律运动。以上健康提示仅为命理参考，如有不适请及时咨询专业医生。',
    balance: '金气适中是理想状态，维持当前节奏即可。您的平衡就是最好的状态。',
  },
  偏弱: {
    title: '金气偏弱',
    summary: '金气稍显不足，决断力有待加强，但这也是内心温柔体贴的一种体现，通过调养可以逐步增强。',
    personality: '可能在需要快速决策时感到困难，对冲突和竞争有所回避。这种特质让你特别善于倾听和感受他人。通过有意识地锻炼决断力，可以逐步增强金气能量。',
    career: '建议主动争取有决策权的机会，锻炼自己的执行力。参加提升效率和纪律性的培训，金气会随实践而增长。',
    emotion: '感情中温柔体贴，但有时过于退让，建议学会适当表达自己的需求，双方关系才会更加平等健康。',
    health: '肺部和大肠需要适当关注，建议加强有氧运动，多呼吸新鲜空气。佩戴金属饰品也有助于补充金气。以上健康提示仅为命理参考，如有不适请及时咨询专业医生。',
    balance: '补充金气：佩戴金银等金属饰品；居住环境增加白色、银色元素；多食辛辣食物；向西方城市发展更利金气。每一步都在增强您的决断力和行动力。',
  },
  极弱: {
    title: '金气极弱',
    summary: '金气极为稀薄，果断与执行力需要后天有意识地培养，这是提升自我的重要方向。',
    personality: '可能在决策和行动上较为迟疑，但这种特质换一个角度，是极为细腻、善于感受他人的宝贵品质。通过建立结构化的习惯和目标，可以弥补金气不足。',
    career: '建议从清晰的目标设定开始，逐步培养执行力。选择有规则框架的工作环境，借助外部结构来补充内在金气的不足。',
    emotion: '感情中需要学会表达坚定的立场，避免一味妥协。适度的原则性反而能让感情关系更加健康。',
    health: '肺部、大肠和皮肤需要重点关注，建议定期体检，加强呼吸系统保健。多接触清新空气，减少污染环境。以上健康提示仅为命理参考，如有不适请及时咨询专业医生。',
    balance: '全面补充金气：大量金属饰品和白色装扮；向西方发展；多食辛辣温热食物；培养严格的自律习惯；每日制定计划并执行，是最好的金气练习。后天的执行力完全可以弥补先天的不足。',
  },
};

const WATER_LEVELS: ElementLevelData = {
  过旺: {
    title: '水气过旺',
    summary: '水气过旺，如汹涌江海，智慧深邃，但也需要一个稳定的河岸来引导方向，才能发挥最大价值。',
    personality: '思维极为敏锐，洞察力惊人，能看透他人难以察觉的深层信息。但有时思虑过多，容易陷入自我怀疑和过度分析中。学会"停止分析，开始行动"，是你最重要的成长课题。',
    career: '在需要深度思维和策略规划的领域极为出色，如哲学研究、商业策略、心理咨询。水旺宜疏导，选择能让你充分运用智慧的职业，可以将旺盛水气转化为实际价值。',
    emotion: '感情中感受力极强，能深刻感知伴侣的情绪，是极度细腻的爱人。但过于敏感有时会产生不必要的误解，学会信任和坦然沟通，感情会更加稳定。',
    health: '肾脏功能偏旺，需注意防止因情绪起伏过大引起的内分泌紊乱。保暖至关重要，尤其是腰部和下肢。减少咸食，保持水分摄入适度。以上健康提示仅为命理参考，如有不适请及时咨询专业医生。',
    balance: '引入土元素来稳定：多亲近山地大地，使用黄色棕色装饰，增加规律作息。将旺盛的水气引导为智慧和创造力，而非无边际的思虑。智慧需要行动来落地。',
  },
  旺盛: {
    title: '水气旺盛',
    summary: '水气旺盛，智慧与适应力是你最大的优势，处世圆融，直觉敏锐，是命局中宝贵的财富。',
    personality: '聪明机智，思维灵活，能在各种环境中游刃有余。直觉准确，常常能在别人还没察觉时就预感到趋势。情商较高，善于与各类人群建立良好关系。',
    career: '在需要策略思维和人际协调的领域大放异彩。水旺之人往往在中年时智慧积累到顶峰，形成强大的竞争优势。',
    emotion: '感情细腻深沉，深爱时会全心付出。建议坦然表达内心感受，不要让伴侣猜测你的情感。',
    health: '肾脏功能良好，注意保暖，适当补充水分，保持规律的睡眠作息。以上健康提示仅为命理参考，如有不适请及时咨询专业医生。',
    balance: '水气旺盛是优势，平衡行动力是关键。有了智慧，再加上执行力，会无往不利。智慧要通过行动才能真正发光。',
  },
  适中: {
    title: '水气适中',
    summary: '水气均衡，智慧与务实并存，是思维灵活而又有执行力的理想状态。',
    personality: '思维敏锐而不失踏实，既有深度思考的能力，又能果断付诸行动。处世圆融，善于化解矛盾，情商高。',
    career: '各行业均能良好适应，尤其在需要策略思维的领域有突出表现。',
    emotion: '感情关系和谐，既有深度也有温度，是非常理想的伴侣特质。',
    health: '肾脏状态平衡，保持规律作息和适量饮水。以上健康提示仅为命理参考，如有不适请及时咨询专业医生。',
    balance: '水气适中是理想状态，维持当前生活节奏即可。您的平衡就是最好的礼物。',
  },
  偏弱: {
    title: '水气偏弱',
    summary: '水气稍显不足，智慧与灵感的流动有待激活，通过调养可以逐步开启内在的智慧泉源。',
    personality: '可能感到思维有时不够灵活，在需要快速变通的情况下显得有些僵化。这是提醒你通过学习和思考来补充水气的信号，知识是最好的水气来源。',
    career: '建议多阅读、学习新知识，让思维保持流动。水气行业（哲学、咨询、IT）可以激活内在智慧。',
    emotion: '感情中可能在情感感知上有些迟钝，需要有意识地关注伴侣的情绪变化，增加情感互动。',
    health: '肾脏功能需要关注，建议注意保暖，避免寒凉食物。补肾食物如黑芝麻、核桃、黑豆对补充水气有益。以上健康提示仅为命理参考，如有不适请及时咨询专业医生。',
    balance: '通过生活方式补充水气：居住靠近水源；家居使用蓝色黑色元素；多食黑色食物和补肾食物；多阅读和思考，保持好奇心。智慧的涌现需要日积月累的滋养。',
  },
  极弱: {
    title: '水气极弱',
    summary: '水气极为微弱，如干涸的河床，但通过积极的调养，智慧之泉完全可以重新涌现。',
    personality: '可能感到思维较为僵化，缺乏灵活变通的能力。但水气弱往往造就务实、脚踏实地的品格，是另一种宝贵的特质。通过大量阅读和跨领域学习，可以逐步开启智慧之门。',
    career: '建议选择需要深度思考的领域持续发展，博学多思是补充水气的最佳方式。同时多与智慧型人士交流，激发内在思维活力。',
    emotion: '感情中需要特别注重情感感知的培养，学会从对方的角度理解问题，增加情感流动性。',
    health: '肾脏功能需要重点养护，建议定期检查，注意腰部保暖，避免过劳。补肾食疗：枸杞、黑豆、核桃、黑芝麻，坚持服用效果良好。以上健康提示仅为命理参考，如有不适请及时咨询专业医生。',
    balance: '全面补充水气：靠近水源居住；大量使用蓝黑色系；每日饮水充足；坚持阅读学习；补肾食疗；保持充足睡眠（肾脏在夜晚修复）。后天的学习与调养，完全可以重新开启智慧之泉。',
  },
};

// ── 组装数据 ─────────────────────────────────────────────────
const ELEMENT_LEVEL_DATA: Record<Element, ElementLevelData> = {
  木: WOOD_LEVELS,
  火: FIRE_LEVELS,
  土: EARTH_LEVELS,
  金: METAL_LEVELS,
  水: WATER_LEVELS,
};

// ── 对外接口 ─────────────────────────────────────────────────

export interface WuxingDetailItem {
  element: Element;
  percent: number;
  count?: number;
  role?: string;
  level: StrengthLevel;
  levelColor: string;
  color: string;
  icon: string;
  fixed: ElementFixedInfo;
  analysis: LevelAnalysis;
}

export interface WuxingSeasonState {
  element: Element;
  state: '旺' | '相' | '休' | '囚' | '死';
  color: string;
}

const ELEMENT_ORDER: Element[] = ['木', '火', '土', '金', '水'];

const BRANCH_MAIN_ELEMENT: Record<Branch, Element> = {
  子: '水', 丑: '土', 寅: '木', 卯: '木',
  辰: '土', 巳: '火', 午: '火', 未: '土',
  申: '金', 酉: '金', 戌: '土', 亥: '水',
};

const MONTH_SEASON_STATES: Record<Branch, Record<Element, WuxingSeasonState['state']>> = {
  寅: { 木: '旺', 火: '相', 水: '休', 金: '囚', 土: '死' },
  卯: { 木: '旺', 火: '相', 水: '休', 金: '囚', 土: '死' },
  巳: { 火: '旺', 土: '相', 木: '休', 水: '囚', 金: '死' },
  午: { 火: '旺', 土: '相', 木: '休', 水: '囚', 金: '死' },
  申: { 金: '旺', 水: '相', 土: '休', 火: '囚', 木: '死' },
  酉: { 金: '旺', 水: '相', 土: '休', 火: '囚', 木: '死' },
  亥: { 水: '旺', 木: '相', 金: '休', 土: '囚', 火: '死' },
  子: { 水: '旺', 木: '相', 金: '休', 土: '囚', 火: '死' },
  辰: { 土: '旺', 金: '相', 火: '休', 木: '囚', 水: '死' },
  戌: { 土: '旺', 金: '相', 火: '休', 木: '囚', 水: '死' },
  丑: { 土: '旺', 金: '相', 火: '休', 木: '囚', 水: '死' },
  未: { 土: '旺', 金: '相', 火: '休', 木: '囚', 水: '死' },
};

function createScores(initial = 0): Record<Element, number> {
  return { 木: initial, 火: initial, 土: initial, 金: initial, 水: initial };
}

function getPillarList(fourPillars: FourPillars) {
  return [fourPillars.year, fourPillars.month, fourPillars.day, fourPillars.hour];
}

function normalizeScores(scores: Record<Element, number>): Record<Element, number> {
  const total = Object.values(scores).reduce((sum, val) => sum + val, 0);
  if (total <= 0) return createScores(0);

  return ELEMENT_ORDER.reduce((acc, el) => {
    acc[el] = Math.max(0, Math.round((scores[el] / total) * 100));
    return acc;
  }, createScores(0));
}

function countPercent(count: number, total: number): number {
  if (total <= 0) return 0;
  return Math.round((count / total) * 1000) / 10;
}

function getElementRole(dayElement: Element, target: Element): string {
  if (target === dayElement) return '比劫';
  if (ELEMENT_FIXED[dayElement].generates === target) return '食伤';
  if (ELEMENT_FIXED[dayElement].overcomes === target) return '财才';
  if (ELEMENT_FIXED[dayElement].generatedBy === target) return '印绶';
  return '官杀';
}

function toDetailItems(
  fourPillars: FourPillars,
  values: Record<Element, number>,
  counts?: Record<Element, number>,
): WuxingDetailItem[] {
  const dayElement = STEM_ELEMENT[fourPillars.day.stem];
  return ELEMENT_ORDER.map(el => {
    const percent = values[el] || 0;
    const level = getStrengthLevel(percent);
    return {
      element: el,
      percent,
      count: counts?.[el],
      role: getElementRole(dayElement, el),
      level,
      levelColor: getStrengthColor(level),
      color: ELEMENT_COLORS[el],
      icon: ELEMENT_ICONS[el],
      fixed: ELEMENT_FIXED[el],
      analysis: ELEMENT_LEVEL_DATA[el][level],
    };
  });
}

export function buildWuxingVisibleCounts(fourPillars: FourPillars): WuxingDetailItem[] {
  const counts = createScores(0);
  const pillars = getPillarList(fourPillars);
  pillars.forEach(p => {
    counts[STEM_ELEMENT[p.stem]] += 1;
    counts[BRANCH_MAIN_ELEMENT[p.branch]] += 1;
  });
  const values = ELEMENT_ORDER.reduce((acc, el) => {
    acc[el] = countPercent(counts[el], 8);
    return acc;
  }, createScores(0));
  return toDetailItems(fourPillars, values, counts);
}

export function buildWuxingHiddenCounts(fourPillars: FourPillars): WuxingDetailItem[] {
  const counts = createScores(0);
  const pillars = getPillarList(fourPillars);
  pillars.forEach(p => {
    counts[STEM_ELEMENT[p.stem]] += 1;
    BRANCH_HIDDEN[p.branch].forEach(hidden => {
      counts[STEM_ELEMENT[hidden.stem]] += 1;
    });
  });
  const total = Object.values(counts).reduce((sum, val) => sum + val, 0);
  const values = ELEMENT_ORDER.reduce((acc, el) => {
    acc[el] = countPercent(counts[el], total);
    return acc;
  }, createScores(0));
  return toDetailItems(fourPillars, values, counts);
}

export function buildWuxingEnergyDetail(fourPillars: FourPillars): WuxingDetailItem[] {
  // 简化分布统计：天干各计 1，地支藏干按 本气/中气/余气 计 1/0.5/0.3，
  // 不做月令系数、能量因子与合冲结构修正。
  const scores = createScores(0);
  const pillars = getPillarList(fourPillars);
  const hiddenWeights = [1, 0.5, 0.3];

  pillars.forEach(p => {
    scores[STEM_ELEMENT[p.stem]] += 1;
    const hiddenStems = BRANCH_HIDDEN[p.branch] || [];
    hiddenStems.forEach((hidden, index) => {
      scores[STEM_ELEMENT[hidden.stem]] += hiddenWeights[index] || 0.2;
    });
  });

  return toDetailItems(fourPillars, normalizeScores(scores));
}

export function getWuxingSeasonStates(monthBranch: Branch): WuxingSeasonState[] {
  const states = MONTH_SEASON_STATES[monthBranch] || MONTH_SEASON_STATES.辰;
  return [...ELEMENT_ORDER]
    .map(element => ({ element, state: states[element], color: ELEMENT_COLORS[element] }))
    .sort((a, b) => {
      const rank: Record<WuxingSeasonState['state'], number> = { 旺: 0, 相: 1, 休: 2, 囚: 3, 死: 4 };
      return rank[a.state] - rank[b.state];
    });
}

/**
 * 获取五行详析数据（基于四柱计算）
 */
export function buildWuxingDetail(fourPillars: FourPillars): WuxingDetailItem[] {
  return buildWuxingEnergyDetail(fourPillars);
}

/**
 * 获取整体五行平衡状态说明（用于页面顶部概述）
 */
export function getWuxingOverview(items: WuxingDetailItem[]): {
  balanceScore: number;
  summary: string;
  mainDeficiency: Element | null;
  mainExcess: Element | null;
} {
  const sorted = [...items].sort((a, b) => b.percent - a.percent);
  const strongest = sorted[0];
  const weakest = sorted[sorted.length - 1];

  const variance = items.reduce((sum, it) => {
    const diff = it.percent - 20;
    return sum + diff * diff;
  }, 0) / items.length;

  const balanceScore = Math.max(0, Math.round(100 - variance / 5));

  let summary = '';
  if (balanceScore >= 85) {
    summary = '五行分布均衡，命局和谐，各方面发展较为全面，适应力强，是难得的平衡命局。';
  } else if (balanceScore >= 65) {
    summary = `命局五行有所侧重，${strongest.element}气较旺，${weakest.element}气偏弱，通过日常调养可以优化五行平衡，进一步提升运势。`;
  } else {
    summary = `命局五行分布差异较大，${strongest.element}气突出，${weakest.element}气明显不足。了解自身五行特质，有针对性地调节，是改善运势的关键。`;
  }

  return {
    balanceScore,
    summary,
    mainExcess: strongest.level === '过旺' || strongest.level === '旺盛' ? strongest.element : null,
    mainDeficiency: weakest.level === '极弱' || weakest.level === '偏弱' ? weakest.element : null,
  };
}
